'use client'

import { type ReactNode, useId } from 'react'
import { cn } from './cn'

/** What a control needs to be tied to its label, hint and error. */
export interface ControlProps {
  id: string
  'aria-describedby'?: string
  'aria-invalid'?: true
  required?: boolean
}

export interface FieldProps {
  label: ReactNode
  /** Shown after the label, e.g. "(facoltativo)"; the caller passes the translated word. */
  optionalLabel?: string
  hint?: ReactNode
  error?: string | null
  required?: boolean
  className?: string
  children: (control: ControlProps) => ReactNode
}

/** A label, a control, a hint and an error, tied together for screen readers. */
export function Field({
  label,
  optionalLabel,
  hint,
  error,
  required = false,
  className,
  children,
}: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-sm font-medium text-text">
        {label}
        {optionalLabel ? (
          <span className="font-regular text-text-muted"> ({optionalLabel})</span>
        ) : null}
      </label>
      {children({
        id,
        'aria-describedby': describedBy,
        ...(error ? { 'aria-invalid': true as const } : {}),
        required,
      })}
      {hint ? (
        <p id={hintId} className="text-xs text-text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-sm font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
